import type { PrebookingDate } from '~/composables/prebooking-date'
import * as Sentry from '@sentry/vue'
import { subHours } from 'date-fns'
import { defineStore } from 'pinia'
import { ExternalControllerApi } from '~/api/prebooking'
import { createDate } from '~/composables/prebooking-date'
import { useTranslations } from '~/composables/translate/translations'
import { useLocationStore } from '~/stores/location'
import { useReferralStore } from '~/stores/referral'

export const DATE_TRANSLATIONS_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: false,
}

export const useDateTimeStore = defineStore('date-time', () => {
  const startDate = reactive<PrebookingDate>(createDate())
  const endDate = reactive<PrebookingDate>(createDate())

  /** Min and max dates for starting date - coming for backend config */
  const minStartingDate = ref<Date | undefined>()
  const maxStartingDate = ref<Date | undefined>()

  /**
   * Min and max dates for ending date - computed based on :
   *   - starting date
   *   - minimal duration (1 day)
   *   - maximal duration: config loaded from backend
   */
  const maxBookingDays = ref(0)
  const minEndingDate = computed<Date | undefined>(() => {
    if (!startDate.date) {
      return undefined
    }

    const newMinEndingDate = new Date(startDate.date)
    newMinEndingDate.setDate(newMinEndingDate.getDate() + 1)
    return newMinEndingDate
  })
  const maxEndingDate = computed<Date | undefined>(() => {
    if (!startDate.date) {
      return undefined
    }

    const newMaxEndingDate = new Date(startDate.date)
    newMaxEndingDate.setDate(newMaxEndingDate.getDate() + (maxBookingDays.value || 1))
    return newMaxEndingDate
  })

  /**
   * Date at which customer can cancel their booking without paying fees
   * Computed based on the start booking date and the cancellation no fee time out (loaded from backend)
   */
  const cancellationNoFeeTimeoutHours = ref<number>(0)
  const noFeeCancellationDate = computed<Date | undefined>(() => {
    if (!startDate.date) {
      return undefined
    }

    const newCancellationDate = new Date(startDate.date)
    return subHours(newCancellationDate, cancellationNoFeeTimeoutHours.value)
  })

  const driverValidationTimeOutHours = ref(0)

  const locationStore = useLocationStore()
  const referralStore = useReferralStore()

  // TODO: get rid of watchEffect. +Produces errors in tests
  watchEffect(async () => {
    try {
      const locationConfig = await new ExternalControllerApi()
        .getLocationConfig({ locationId: locationStore.city.locationId })

      if (!startDate.date) {
        startDate.dateTime = createDate(locationConfig.earliestPrebookingPossibleAt).date

        const newEndDate = createDate(locationConfig.earliestPrebookingPossibleAt).date!
        newEndDate.setDate(newEndDate.getDate() + 7)
        endDate.dateTime = newEndDate
      }

      // Determine which date constraints to use based on meta flow
      const shouldUseMetaDates = referralStore.isMetaFlow && useRuntimeConfig().public.META_OFFERS_ENABLED
      const earliestDate = shouldUseMetaDates
        ? locationConfig.earliestPrebookingPossibleAtMeta
        : locationConfig.earliestPrebookingPossibleAt
      const latestDate = shouldUseMetaDates
        ? locationConfig.latestPrebookingPossibleAtMeta
        : locationConfig.latestPrebookingPossibleAt

      minStartingDate.value = createDate(earliestDate, { minutesRounding: 'later' }).date
      maxStartingDate.value = createDate(latestDate, { minutesRounding: 'sooner' }).date
      maxBookingDays.value = locationConfig.maxBookingDays
      driverValidationTimeOutHours.value = locationConfig.driverNotValidatedTimeout
      cancellationNoFeeTimeoutHours.value = locationConfig.cancellationNoFeeTimeoutHours
    }
    catch (error) {
      Sentry.captureException(error)
      console.error('Error while loading location configuration', error)
    }
  })

  const errorStartingDate = computed<string | undefined>(() => {
    if (!startDate.date || !minStartingDate.value || !maxStartingDate.value) {
      return undefined
    }

    const language = useTranslations().language.value

    if (startDate.date < minStartingDate.value) {
      return translate(
        'error_too_soon_to_prebook',
        { '{DATE}': minStartingDate.value!.toLocaleDateString(language, DATE_TRANSLATIONS_OPTIONS) },
      )
    }

    if (startDate.date > maxStartingDate.value) {
      return translate(
        'error_prebook_start_too_far_away',
        { '{DATE}': maxStartingDate.value.toLocaleDateString(language, DATE_TRANSLATIONS_OPTIONS) },
      )
    }

    return undefined
  })

  const errorEndingDate = computed<string | undefined>(() => {
    if (!endDate.date || !minEndingDate.value || !maxEndingDate.value) {
      return undefined
    }

    if (endDate.date > maxEndingDate.value) {
      return translate(
        'error_max_booking_duration_exceeded',
        { '{days}': String(maxBookingDays.value) },
      )
    }

    return undefined
  })

  const errorBookingPeriod = computed<string | undefined>(() => {
    if (!startDate.date || !endDate.date) {
      return undefined
    }

    if (startDate.date >= endDate.date) {
      return translate('error_booking_period')
    }
  })

  const areDatesValid = computed(() =>
    !!startDate.date && !!endDate.date
    && !errorStartingDate.value && !errorEndingDate.value && !errorBookingPeriod.value,
  )

  return {
    startDate,
    minStartingDate: readonly(minStartingDate),
    maxStartingDate: readonly(maxStartingDate),
    errorStartingDate: readonly(errorStartingDate),
    errorEndingDate: readonly(errorEndingDate),
    errorBookingPeriod: readonly(errorBookingPeriod),
    endDate,
    minEndingDate,
    maxEndingDate,
    noFeeCancellationDate,
    cancellationNoFeeTimeoutHours: readonly(cancellationNoFeeTimeoutHours),
    areDatesValid,
  }
})
